import React, { useState } from 'react';

import { State, flowActions, selectCommandByNodeId, tabActions } from '@store/index';
import { useDispatch, useSelector } from 'react-redux';
import { NodeSidebarHeader } from '@common/Node/NodeSidebar/NodeSidebarHeader';
import { NodeSidebarContainer } from '@common/Node/NodeSidebar/NodeSidebarContainer';
import { HTTPTabs } from './Tabs';
import { executeNode } from '@requests/nodes';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@common/hooks/useTranslate';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { NodeTestRun } from '@components/Modals/NodeTestRun';
import { useNodeError } from '@common/hooks/useNodeError';

export const HTTPSidebar: React.FC = () => {
  const dispatch = useDispatch();

  const { translate } = useTranslate();
  const { service_id, workflow_id } = useParams();
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState('1');

  const queryClient = useQueryClient();
  const selectedNode = useSelector((state: State) => state.tabs.active.selectedNode);
  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));

  const nodesLoading = useSelector((state: State) => state.tabs.active.nodesLoading);

  const { mutateAsync: execute, isPending: loading } = useMutation({
    mutationFn: executeNode,
    onMutate: () => {
      if (command?.node) dispatch(tabActions.setNodeLoading(command.node.id));
    },
    onSuccess: (data) => {
      if (data?.error) {
        if (command) dispatch(tabActions.stopNodeLoading(command.node.id));

        enqueueSnackbar(data?.error || 'An unexpected error occured.', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        enqueueSnackbar('Failed to execute node.', {
          variant: 'error',
          autoHideDuration: 10000,
        });

        return;
      }

      queryClient.invalidateQueries({ queryKey: ['workflows'] });

      if (command) {
        enqueueSnackbar('Node executed successfully.', {
          variant: 'success',
          autoHideDuration: 3000,
        });

        const { node_id, ...dataWithoutNodeId } = data;
        dispatch(tabActions.refreshDynamicNodeData({ node_id, output: dataWithoutNodeId }));
        dispatch(tabActions.stopNodeLoading(command.node.id));
        setTab('3');
      }
    },
  });

  const { nodeStatus } = useNodeError({ command });

  if (!command) return <NodeSidebarContainer />;

  const isLoading = loading || nodesLoading.includes(command.node.id) || nodeStatus === 'processing';

  const runNode = () => {
    const translation = translate(workflow_id as string);
    const node = translation.nodes.find((node) => node.node_id === selectedNode);

    if (!node) return;

    if (node.variables.length > 0 && node) {
      return dispatch(flowActions.setNodeTestRun(node));
    }

    execute({ service_id, node, plugins: [], edges: [] });
  };

  return (
    <NodeSidebarContainer>
      <NodeTestRun setTab={setTab} />
      <NodeSidebarHeader command={command} execution={{ run: runNode, loading: isLoading, customColor: 'purple' }} />
      <HTTPTabs tab={tab} setTab={setTab} dataLoading={loading} />
    </NodeSidebarContainer>
  );
};
